import { MRT_ColumnDef, createMRTColumnHelper } from "material-react-table";
import { GridText } from "src/components/Grid/GridText";
import { useTranslation } from "react-i18next";
import { MrtColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { DataListValueOverride } from "src/api/client-api/dataListValueOverride/useGetAllDataListValueOverride";
import { Chip, CircularProgress } from "@mui/material";
import { DataListVersionWithDataList } from "src/api/sure-api/dataList/useGetAllDataListVersions";

export const useDataListValueOverridesColumns = (
    dataListVersions?: DataListVersionWithDataList[],
): MrtColumns<DataListValueOverride> => {
    const { t } = useTranslation();

    const columnHelper = createMRTColumnHelper<DataListValueOverride>();

    const mrt_columns = [
        columnHelper.accessor((row) => row.id, {
            id: "id",
            header: t("settings.data_lists_overrides.id", "ID"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
            size: 10,
        }),
        columnHelper.accessor((row) => row.dataListVersionId, {
            id: "dataListVersionId",
            header: t("settings.data_lists_overrides.version_id", "Version ID"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
            size: 10,
        }),
        columnHelper.accessor((row) => row.dataListVersionId, {
            id: "dataListVersion",
            header: t("settings.data_lists_overrides.version", "Version"),
            Cell: (value) => {
                const dataListVersion = dataListVersions?.find(
                    (dataListVersion) => dataListVersion.id === value.renderedCellValue,
                );

                if (!dataListVersion) return <CircularProgress size={20} />;

                return <GridText value={`v${dataListVersion.versionMajor}.${dataListVersion.versionMinor}`} />;
            },
        }),
        columnHelper.accessor((row) => row.dataListVersionId, {
            id: "title",
            header: t("settings.data_lists_overrides.title", "Title"),
            Cell: (value) => {
                const dataListVersion = dataListVersions?.find(
                    (dataListVersion) => dataListVersion.id === value.renderedCellValue,
                );

                if (!dataListVersion) return <CircularProgress size={20} />;

                return <GridText value={dataListVersion.title} />;
            },
        }),
        columnHelper.accessor((row) => row.dataListVersionId, {
            id: "dataList",
            header: t("settings.data_lists_overrides.data_list", "Data list"),
            Cell: (value) => {
                const dataListVersion = dataListVersions?.find(
                    (dataListVersion) => dataListVersion.id === value.renderedCellValue,
                );

                if (!dataListVersion) return <CircularProgress size={20} />;

                return <GridText value={dataListVersion.dataList.code} />;
            },
        }),
    ] as MRT_ColumnDef<DataListValueOverride, any>[];

    mrt_columns.push(
        columnHelper.accessor("dataListOptionValueOverrides", {
            header: t("settings.data_lists_overrides.overrides_options", "Overrides data list options?"),
            Cell: (value) => (
                <Chip
                    label={value.cell.getValue().length > 0 ? "Yes" : "No"}
                    color={value.cell.getValue().length > 0 ? "success" : "default"}
                    size="small"
                    variant="outlined"
                />
            ),
        }),
    );

    mrt_columns.push(
        columnHelper.accessor("dataListExtraOptions", {
            header: t("settings.data_lists_overrides.has_extra_options", "Has extra options?"),
            Cell: (value) => (
                <Chip
                    label={value.cell.getValue().length > 0 ? "Yes" : "No"}
                    color={value.cell.getValue().length > 0 ? "success" : "default"}
                    size="small"
                    variant="outlined"
                />
            ),
        }),
    );

    return mrt_columns as MRT_ColumnDef<DataListValueOverride, string>[];
};
